import { Activity, Calculator, Coins, Gem, Landmark, Laptop, Smartphone, ChevronDown, Ticket, Wallet } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

type BilingualOpt = {
  to: string;
  flag: string;
  english: string;
  native: string;
  nativeLang: "ur" | "hi" | "tr";
  nativeDir: "rtl" | "ltr";
};

const GOLD_OPTS: BilingualOpt[] = [
  { to: "/pakistan", flag: "🇵🇰", english: "Gold Price in Pakistan", native: "پاکستان میں سونے کی قیمت", nativeLang: "ur", nativeDir: "rtl" },
  { to: "/india", flag: "🇮🇳", english: "Gold Price in India", native: "भारत में सोने का भाव", nativeLang: "hi", nativeDir: "ltr" },
  { to: "/uae", flag: "🇦🇪", english: "Gold Price in UAE", native: "أسعار الذهب في الإمارات", nativeLang: "ur", nativeDir: "rtl" },
  { to: "/ar/gold-rate-saudi-arabia", flag: "🇸🇦", english: "Gold Price in Saudi Arabia", native: "أسعار الذهب في السعودية", nativeLang: "ur", nativeDir: "rtl" },
  { to: "/tr/canli-gram-altin-fiyatlari", flag: "🇹🇷", english: "Gold Price in Turkey", native: "Canlı Gram Altın Fiyatları", nativeLang: "tr", nativeDir: "ltr" },
];
const SILVER_OPTS: BilingualOpt[] = [
  { to: "/silver-rate-pakistan", flag: "🇵🇰", english: "Silver Price in Pakistan", native: "پاکستان میں چاندی کی قیمت", nativeLang: "ur", nativeDir: "rtl" },
  { to: "/silver-rate-india", flag: "🇮🇳", english: "Silver Price in India", native: "भारत में चांदी का भाव", nativeLang: "hi", nativeDir: "ltr" },
];

export function GlobalHero() {
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <section className="relative z-40 pt-8 md:pt-12 pb-10">
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden hidden md:block">
        <div
          className="absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[820px] rounded-full opacity-25 blur-2xl"
          style={{ background: "radial-gradient(circle, #3b82f6 0%, transparent 70%)" }}
        />
      </div>
      <div className="relative z-40 mx-auto max-w-6xl px-4">
        <div className="glass rounded-3xl border border-border/60 p-6 md:p-10 relative z-40">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-bold tracking-widest uppercase text-primary">
              <Activity className="h-3.5 w-3.5" /> Global Smart Dashboard
            </div>
            <h1 className="mt-4 text-3xl md:text-5xl font-black tracking-tight leading-[1.05]">
              DrHint — Real-Time Global{" "}
              <span className="text-gold">Utilities &amp; Financial</span> Toolbox
            </h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base text-muted-foreground">
              One neutral command centre for live precious-metal markets,
              loan and everyday calculators, and honest online-work guides — all delivered without locking you into a single country.
            </p>
          </div>

          <div className="mt-7 flex flex-wrap gap-2">
            <BilingualChip
              id="gold"
              icon={Coins}
              label="Check Gold Price"
              options={GOLD_OPTS}
              openId={openId}
              setOpenId={setOpenId}
              align="left"
            />
            <BilingualChip
              id="silver"
              icon={Gem}
              label="Check Silver Price"
              options={SILVER_OPTS}
              openId={openId}
              setOpenId={setOpenId}
              align="right"
            />
            <HeroChip to="/prize-bond" icon={Ticket} label="Prize Bond Checker" />
            <HeroChip to="/earning-apps-review" icon={Wallet} label="Earning Apps Review" />
            <HeroChip to="/calculators" icon={Calculator} label="All Calculators Hub" />
            <HeroChip to="/remote-jobs" icon={Laptop} label="Non-Phone Remote Job Portals" />
            <HeroChip to="/app-testing" icon={Smartphone} label="Verified App & Software Testing Directory" />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroChip({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/60 px-3 py-1.5 text-xs font-semibold hover:border-primary/40 hover:bg-background"
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </Link>
  );
}

function BilingualChip({
  id,
  icon: Icon,
  label,
  options,
  openId,
  setOpenId,
  align = "left",
}: {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  options: BilingualOpt[];
  openId: string | null;
  setOpenId: (v: string | null) => void;
  align?: "left" | "right";
}) {
  const open = openId === id;
  const wrapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpenId(null);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpenId(null);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, setOpenId]);
  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpenId(open ? null : id)}
        aria-expanded={open}
        aria-haspopup="true"
        className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/60 px-3 py-1.5 text-xs font-semibold hover:border-primary/40 hover:bg-background"
      >
        <Icon className="h-3.5 w-3.5" /> {label}
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          role="menu"
          className={`absolute z-[200] mt-2 w-[min(88vw,360px)] max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto overscroll-contain rounded-2xl border border-border/70 bg-card/95 p-2 shadow-2xl backdrop-blur grid gap-2 sm:grid-cols-2 ${align === "right" ? "right-0" : "left-0"}`}
        >
          {options.map((o) => (
            <Link
              key={o.to}
              to={o.to}
              onClick={() => setOpenId(null)}
              className="block rounded-xl border border-border/60 bg-background/80 p-3 hover:border-primary/50 hover:bg-primary/5 transition-colors"
            >
              <div className="flex items-center gap-2 text-sm font-semibold">
                <span className="text-base leading-none" aria-hidden>{o.flag}</span>
                <span>{o.english}</span>
              </div>
              <div
                lang={o.nativeLang}
                dir={o.nativeDir}
                className={`mt-1 text-xs text-muted-foreground ${o.nativeDir === "rtl" ? "text-right" : ""}`}
              >
                {o.native}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}